'use client'

import { TrackingInsiderObject } from '@/components/analytics/tracking-insider-object'
import { ProductCard } from '@/components/common'
import { ProductCardSkeleton } from '@/components/common/product-card/product-item-skeleton'
import { Button } from '@/components/ui/button'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { TCollectionData } from '@/types/collection'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useMemo } from 'react'
import { useCollectionData } from '../hooks/use-collection-data'
import {
  hasActiveFilters,
  useCollectionFilterStoreV2,
} from '../store/collection-filter-store-v2'
import { CollectionRandomBannerCard } from './collection-random-banner-card'
import { CollectionEmptyProductList } from './empty-product-list'

type Props = {
  className?: string
  serverData: TCollectionData
  initialFilters?: Record<string, string | string[] | number | undefined>
}

export const CollectionListing = ({ className, serverData }: Props) => {
  const t = useTranslations()

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useCollectionData({ serverData })

  const filters = useCollectionFilterStoreV2((state) => state.filters)

  const allProducts = useMemo(
    () => data?.pages.flatMap((page) => page?.products ?? []) ?? [],
    [data?.pages]
  )

  const pagination = data?.pages?.[data.pages.length - 1]?.pagination

  const finalClass = cn(
    'grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5',
    className
  )

  const mixedItems = useMemo(() => {
    if (hasActiveFilters(filters)) return allProducts

    const bannerData = serverData?.collection_detail?.random_banner
    const banners = bannerData?.hide
      ? []
      : [...(bannerData?.images || [])].sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        )

    if (!banners.length) return allProducts

    const items: Array<
      | (typeof allProducts)[0]
      | {
          isBanner: true
          banner: import('@/types/collection').TRandomBannerImageItem
        }
    > = []

    let prodIdx = 0
    let bannerIdx = 0
    let currentPos = 1

    while (prodIdx < allProducts.length || bannerIdx < banners.length) {
      if (
        bannerIdx < banners.length &&
        banners[bannerIdx].order === currentPos &&
        allProducts.length >= banners[bannerIdx].order
      ) {
        items.push({ isBanner: true, banner: banners[bannerIdx++] })
      } else if (prodIdx < allProducts.length) {
        items.push(allProducts[prodIdx++])
      } else {
        break
      }
      currentPos++
    }

    return items
  }, [allProducts, serverData?.collection_detail?.random_banner, filters])

  if (!serverData) {
    return null
  }

  // Show loading state when:
  // 1. Initial loading (isLoading && no products) AND no serverData fallback
  // 2. Refetching with no products AND no serverData fallback
  // 3. Refetching with products (filter/sort changed) - show skeleton to replace old products
  // Note: Don't show loading if we have serverData fallback (navigate back scenario)
  const shouldShowLoading =
    (isLoading && allProducts.length === 0) ||
    (isFetching && allProducts.length === 0)

  // Show skeleton when refetching and has products (filter/sort changed)
  const shouldShowSkeletonOnRefetch =
    isFetching && !isFetchingNextPage && allProducts.length > 0

  if (shouldShowLoading) {
    return (
      <div className='w-full flex-1' id='collection-listing'>
        <div className={finalClass}>
          {Array.from({ length: 12 }).map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      </div>
    )
  }

  // If refetching with products, show skeleton instead of old products
  if (shouldShowSkeletonOnRefetch) {
    return (
      <div className='w-full flex-1' id='collection-listing'>
        <div className={finalClass}>
          {Array.from({ length: allProducts.length || 12 }).map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      </div>
    )
  }

  const insiderObjectListing = {
    items: allProducts.map((product) => ({
      id: product.id,
      name: product.title,
      currency: 'VND',
      taxonomy: [],
      unit_price: product.compare_price,
      unit_sale_price: product.regular_price,
      url: `https://www.coolmate.me${product.href}`,
      product_image_url: ImageHelper.getImageUrl(product.default_thumbnail)
        .replace(/\(/g, '%28')
        .replace(/\)/g, '%29'),
    })),
  }

  const currentShowing = allProducts.length
  const totalProducts = pagination?.total ?? 0

  return (
    <>
      <div className='w-full flex-1' id='collection-listing'>
        <div className={finalClass}>
          {mixedItems.map((item, index) => {
            if ('isBanner' in item) {
              return (
                <CollectionRandomBannerCard
                  key={`banner-${index}`}
                  banner={item.banner}
                  collectionTitle={serverData.collection_detail?.title}
                />
              )
            }
            return <ProductCard product={item} key={`${item.id}-${index}`} />
          })}
          {allProducts.length === 0 && !isFetching && !isFetchingNextPage && (
            <CollectionEmptyProductList />
          )}
          {isFetchingNextPage &&
            Array.from({ length: 4 }).map((_, index) => (
              <ProductCardSkeleton key={`loading-${index}`} />
            ))}
        </div>
        {hasNextPage && (
          <div className='mt-10 text-center lg:mt-17'>
            <Button
              variant='black'
              className='group'
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {t('button.view_more')}{' '}
              <Image
                src='/icons/icon-arrow-line-right.svg'
                width={16}
                height={19}
                alt='icon arrow right'
                className='mb-1 h-5 w-4 transition-transform duration-200 group-hover:translate-x-1'
              />
            </Button>
            <p className='mt-1 font-sans text-sm leading-4.5 text-neutral-900/60 lg:mt-1.5 lg:text-base lg:leading-5'>
              {t('collection_page.message.display_product_count', {
                currentShowing,
                totalProducts,
              })}
            </p>
          </div>
        )}
      </div>
      <TrackingInsiderObject keyName='listing' value={insiderObjectListing} />
    </>
  )
}
